import React, { memo, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useUser } from "@clerk/clerk-react";
import { fetchTeacherUnread } from "../utils/api";

const POLL_MS = 300000;

let listeners = new Set();
let refCount = 0;
let timer = null;
let activeUserId = null;
let inflight = null;
let count = 0;
let lastFetchAt = 0;

const MIN_GAP_MS = 15000;

const emit = () => {
  listeners.forEach((fn) => fn(count));
};

const refresh = ({ force = false } = {}) => {
  if (!activeUserId || inflight) return;
  if (!force && Date.now() - lastFetchAt < MIN_GAP_MS) return;
  lastFetchAt = Date.now();
  inflight = fetchTeacherUnread()
    .then((data) => {
      count = Number(data.unread) || 0;
      emit();
    })
    .catch(() => {
      // Offline or cold backend: keep the last known count.
    })
    .finally(() => {
      inflight = null;
    });
};

const onFocus = () => refresh();
const onUnreadChanged = () => refresh({ force: true });
const onInterval = () => {
  if (!document.hidden) refresh();
};

const subscribe = (fn) => {
  listeners.add(fn);
  fn(count);
  return () => listeners.delete(fn);
};

const start = (userId) => {
  activeUserId = userId;
  refCount += 1;
  if (refCount === 1) {
    timer = setInterval(onInterval, POLL_MS);
    window.addEventListener("focus", onFocus);
    window.addEventListener("teacher-unread-changed", onUnreadChanged);
  }
  refresh();
};

const stop = () => {
  refCount = Math.max(0, refCount - 1);
  if (refCount === 0) {
    clearInterval(timer);
    timer = null;
    activeUserId = null;
    window.removeEventListener("focus", onFocus);
    window.removeEventListener("teacher-unread-changed", onUnreadChanged);
  }
};

const TeacherMessagesBell = memo(({ label }) => {
  const { user, isSignedIn } = useUser();
  const [displayCount, setDisplayCount] = useState(count);

  useEffect(() => {
    if (!isSignedIn || !user?.id) {
      setDisplayCount(0);
      return undefined;
    }
    const unsubscribe = subscribe(setDisplayCount);
    start(user.id);
    return () => {
      stop();
      unsubscribe();
    };
  }, [isSignedIn, user?.id]);

  if (!isSignedIn) return null;

  return (
    <Link
      to="/contact"
      className="relative p-2 text-[var(--text-muted)] hover:text-[var(--text-heading)] transition"
      title={label}
      aria-label={displayCount > 0 ? `${label} (${displayCount})` : label}
    >
      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M14.857 17.082a23.848 23.848 0 0 0 5.454-1.31A8.967 8.967 0 0 1 18 9.75V9A6 6 0 0 0 6 9v.75a8.967 8.967 0 0 1-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 0 1-5.714 0m5.714 0a3 3 0 1 1-5.714 0" />
      </svg>
      {displayCount > 0 && (
        <span className="absolute top-0 end-0 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">
          {displayCount > 9 ? "9+" : displayCount}
        </span>
      )}
    </Link>
  );
});

export default TeacherMessagesBell;
